import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import JsPDF from 'jspdf';
import {
  getAllBeneficiaries,
  getControlSocialActivities,
  getSchools
} from '../../API/apiService';
import DateRangeFilter from '../../components/dateRangeFilter';
import {
  buildAdminPathWithScope,
  buildScopedGroupEntries,
  buildScopedSchools,
  filterConfirmedSchools,
  isMedellinTerritoryId,
  matchesBeneficiaryByAdminScope,
  normalizeAdminScope,
  resolveGroupMunicipalityId
} from '../../utils/adminScope';
import {
  clearDateFiltersFromSearchParams,
  filterRecordsByDateRange,
  filterSchoolsByGroupFirstMeetingDate,
  formatDateRangeLabel,
  getDateFiltersFromSearchParams,
  getRecordCreatedDate,
  setDateFilterInSearchParams
} from '../../utils/dateFilters';
import { isGroupCancelled } from '../../utils/groupStatus';
import './style.scss';

const PROCESS_GROUP_GOALS = {
  general: 402,
  pf: 128,
  ndn: 100
};

const TEACHER_GROUP_CODE_PATTERN = /GD\d+$/;
const STUDENT_GROUP_CODE_PATTERN = /G\d+$/;

const COMMUNE_LAYOUT = [
  {
    id: 1,
    shortName: 'Popular',
    bubbleTop: '14%',
    bubbleLeft: '66%',
    labelTop: '13%',
    labelLeft: '74%',
    color: '#a88ef2'
  },
  {
    id: 2,
    shortName: 'Santa Cruz',
    bubbleTop: '8%',
    bubbleLeft: '73%',
    labelTop: '6%',
    labelLeft: '80%',
    color: '#9a86ec'
  },
  {
    id: 3,
    shortName: 'Manrique',
    bubbleTop: '32%',
    bubbleLeft: '69%',
    labelTop: '33%',
    labelLeft: '78%',
    color: '#f2ac67'
  },
  {
    id: 4,
    shortName: 'Aranjuez',
    bubbleTop: '28%',
    bubbleLeft: '58%',
    labelTop: '27%',
    labelLeft: '64%',
    color: '#f3d26f'
  },
  {
    id: 5,
    shortName: 'Castilla',
    bubbleTop: '18%',
    bubbleLeft: '50%',
    labelTop: '15%',
    labelLeft: '45%',
    color: '#7ec8ef'
  },
  {
    id: 6,
    shortName: 'Doce de Octubre',
    bubbleTop: '12%',
    bubbleLeft: '42%',
    labelTop: '9%',
    labelLeft: '46%',
    color: '#7abcf0'
  },
  {
    id: 7,
    shortName: 'Robledo',
    bubbleTop: '24%',
    bubbleLeft: '41%',
    labelTop: '22%',
    labelLeft: '44%',
    color: '#86d6e9'
  },
  {
    id: 8,
    shortName: 'Villa Hermosa',
    bubbleTop: '49%',
    bubbleLeft: '68%',
    labelTop: '50%',
    labelLeft: '76%',
    color: '#f0b17b'
  },
  {
    id: 9,
    shortName: 'Buenos Aires',
    bubbleTop: '75%',
    bubbleLeft: '66%',
    labelTop: '80%',
    labelLeft: '69%',
    color: '#f6c07d'
  },
  {
    id: 10,
    shortName: 'La Candelaria',
    bubbleTop: '52%',
    bubbleLeft: '56%',
    labelTop: '57%',
    labelLeft: '52%',
    color: '#f1db77'
  },
  {
    id: 11,
    shortName: 'Laureles',
    bubbleTop: '62%',
    bubbleLeft: '32%',
    labelTop: '62%',
    labelLeft: '37%',
    color: '#86a5ff'
  },
  {
    id: 12,
    shortName: 'La America',
    bubbleTop: '65%',
    bubbleLeft: '28%',
    labelTop: '65%',
    labelLeft: '35%',
    color: '#88a7ff'
  },
  {
    id: 13,
    shortName: 'San Javier',
    bubbleTop: '55%',
    bubbleLeft: '18%',
    labelTop: '54%',
    labelLeft: '24%',
    color: '#73e1cf'
  },
  {
    id: 14,
    shortName: 'El Poblado',
    bubbleTop: '70%',
    bubbleLeft: '79%',
    labelTop: '69%',
    labelLeft: '86%',
    color: '#f5a2be'
  },
  {
    id: 15,
    shortName: 'Guayabal',
    bubbleTop: '84%',
    bubbleLeft: '50%',
    labelTop: '88%',
    labelLeft: '54%',
    color: '#ee9fbf'
  },
  {
    id: 16,
    shortName: 'Belen',
    bubbleTop: '90%',
    bubbleLeft: '37%',
    labelTop: '92%',
    labelLeft: '38%',
    color: '#ea90b0'
  }
];

const PARTICIPATION_ANALYSIS_CARDS = [
  {
    icon: 'monitoring',
    title: 'Analisis de Caracterizados',
    path: '/administrador/avances/analisis-caracterizados',
    description:
      'Espacio reservado para consolidar perfiles, distribuciones y patrones de la poblacion caracterizada.'
  },
  {
    icon: 'public',
    title: 'Analisis de cobertura',
    path: '/administrador/avances/analisis-cobertura',
    description:
      'Aqui podremos visualizar alcance territorial, presencia por territorio e instituciones intervenidas.'
  }
];

const GENERIC_DASHBOARD_COPY = {
  cs: {
    eyebrow: 'Dashboard de avances',
    processLabel: 'Control Social',
    description:
      'Seguimiento de procesos formativos, caracterizaciones y asistencias de Control Social.'
  },
  ndn: {
    eyebrow: 'Dashboard de avances',
    processLabel: 'Nada de Nervios',
    description:
      'Esta pagina quedo lista como base para construir la visualizacion de metricas, indicadores y seguimiento del proceso.'
  },
  cop: {
    eyebrow: 'Dashboard de avances',
    processLabel: 'Centro de Orientacion Pedagogica',
    description:
      'Vista base para consultar el seguimiento de asesorias, sesiones y caracterizaciones del COP.'
  },
  default: {
    eyebrow: 'Dashboard de avances',
    processLabel: 'Vista general',
    description:
      'Esta pagina quedo lista como punto de entrada para construir el tablero de visualizacion y metricas.'
  }
};

const PROCESS_REPORT_LABELS = {
  general: 'Panel general - Escuelas y Nada de Nervios',
  pf: 'Escuelas de Participacion',
  ndn: 'Nada de Nervios',
  cs: 'Control Social',
  cop: 'Centro de Orientacion Pedagogica'
};

function formatNumber(value) {
  return new Intl.NumberFormat('es-CO').format(Number(value ?? 0));
}

function formatReportDate(value = new Date()) {
  return new Intl.DateTimeFormat('es-CO', {
    dateStyle: 'long',
    timeStyle: 'short'
  }).format(value);
}

function formatFileDate(value = new Date()) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  const hours = String(value.getHours()).padStart(2, '0');
  const minutes = String(value.getMinutes()).padStart(2, '0');

  return `${year}${month}${day}-${hours}${minutes}`;
}

function drawPdfCard(doc, x, y, width, height, { title, value, helper }) {
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(204, 233, 245);
  doc.roundedRect(x, y, width, height, 16, 16, 'FD');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(90, 118, 136);
  doc.text(title, x + 14, y + 18);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.setTextColor(23, 50, 71);
  doc.text(String(value), x + 14, y + 44);

  if (helper) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(90, 118, 136);
    const helperLines = doc.splitTextToSize(helper, width - 28);
    doc.text(helperLines, x + 14, y + height - 16);
  }
}

function drawPdfMiniStat(doc, x, y, width, height, { title, value }) {
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(204, 233, 245);
  doc.roundedRect(x, y, width, height, 14, 14, 'FD');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(90, 118, 136);
  doc.text(String(title), x + 12, y + 16);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(23, 50, 71);
  doc.text(String(value), x + 12, y + 39);
}

function downloadParticipationDashboardPdf({ scope, summary, dateFilters }) {
  const processGoal = PROCESS_GROUP_GOALS[scope] || PROCESS_GROUP_GOALS.pf;
  const processLabel = PROCESS_REPORT_LABELS[scope] || 'Dashboard de avances';
  const schoolsIntervenedLabel =
    scope === 'ndn' || scope === 'general'
      ? 'Instituciones intervenidas'
      : 'IE intervenidas';
  const generatedAt = new Date();
  const goalPercentage = Math.round(
    Math.min(summary.totalGroups / processGoal, 1) * 100
  );
  const groupsRemaining = Math.max(processGoal - summary.totalGroups, 0);
  const fileName = `dashboard-${scope}-${formatFileDate(generatedAt)}.pdf`;

  const doc = new JsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 30;
  const gutter = 14;
  const contentWidth = pageWidth - margin * 2;
  const progressCardWidth = contentWidth * 0.43;
  const progressCardHeight = 190;
  const summaryGridWidth = contentWidth - progressCardWidth - gutter;
  const smallCardWidth = (summaryGridWidth - gutter) / 2;
  const topCardHeight = 92;
  const bottomCardHeight = 82;
  const secondaryCardWidth = (contentWidth - gutter * 2) / 3;

  doc.setFillColor(237, 248, 251);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  doc.setFillColor(24, 57, 85);
  doc.roundedRect(margin, margin, contentWidth, 82, 24, 24, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.setTextColor(255, 255, 255);
  doc.text(processLabel, margin + 24, margin + 34);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(231, 244, 250);
  doc.text(
    `Dashboard de avances generado el ${formatReportDate(generatedAt)}`,
    margin + 24,
    margin + 56
  );

  doc.setFontSize(9);
  doc.text(formatDateRangeLabel(dateFilters), margin + 24, margin + 72);

  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(204, 233, 245);
  doc.roundedRect(
    margin,
    margin + 98,
    progressCardWidth,
    progressCardHeight,
    22,
    22,
    'FD'
  );

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(23, 50, 71);
  doc.text('Avance de meta de grupos', margin + 20, margin + 126);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(90, 118, 136);
  doc.text(
    `Meta del proceso: ${formatNumber(processGoal)} grupos creados`,
    margin + 20,
    margin + 146
  );

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(44);
  doc.setTextColor(26, 168, 223);
  doc.text(`${goalPercentage}%`, margin + 20, margin + 215);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.setTextColor(90, 118, 136);
  doc.text('de la meta', margin + 23, margin + 235);

  const goalStatsStartX = margin + Math.min(136, progressCardWidth * 0.4);
  const goalStatsEndX = margin + progressCardWidth - 18;
  const goalStatsWidth = goalStatsEndX - goalStatsStartX;
  const goalStatGap = 10;
  const goalTopRowWidth = (goalStatsWidth - goalStatGap) / 2;
  const goalTopRowY = margin + 158;
  const goalBottomRowY = goalTopRowY + 54;
  const goalMiniStatHeight = 44;

  drawPdfMiniStat(doc, goalStatsStartX, goalTopRowY, goalTopRowWidth, goalMiniStatHeight, {
    title: 'META',
    value: formatNumber(processGoal)
  });
  drawPdfMiniStat(
    doc,
    goalStatsStartX + goalTopRowWidth + goalStatGap,
    goalTopRowY,
    goalTopRowWidth,
    goalMiniStatHeight,
    {
      title: 'CREADOS',
      value: formatNumber(summary.totalGroups)
    }
  );
  drawPdfMiniStat(doc, goalStatsStartX, goalBottomRowY, goalStatsWidth, goalMiniStatHeight, {
    title: 'FALTAN',
    value: formatNumber(groupsRemaining)
  });

  const topCards =
    scope === 'pf'
      ? [
          {
            title: 'Comunas intervenidas',
            value: formatNumber(summary.territoriesIntervened),
            helper: 'Comunas o corregimientos con al menos un grupo creado'
          },
          {
            title: schoolsIntervenedLabel,
            value: formatNumber(summary.schoolsIntervened),
            helper: 'Instituciones con grupos activos'
          },
          {
            title: 'Estudiantes intervenidos',
            value: formatNumber(summary.studentIntervened),
            helper: 'Caracterizados en grupos de estudiantes'
          },
          {
            title: 'Docentes intervenidos',
            value: formatNumber(summary.teacherIntervened),
            helper: 'Caracterizados en grupos de docentes'
          }
        ]
      : [
          {
            title: 'Comunas intervenidas',
            value: formatNumber(summary.territoriesIntervened),
            helper: 'Comunas o corregimientos con al menos un grupo creado'
          },
          {
            title: schoolsIntervenedLabel,
            value: formatNumber(summary.schoolsIntervened),
            helper: 'Instituciones con grupos activos'
          },
          {
            title: 'Personas caracterizadas',
            value: formatNumber(summary.characterizedPeople),
            helper: 'Poblacion caracterizada del proceso'
          },
          {
            title: 'Total de grupos creados',
            value: formatNumber(summary.totalGroups),
            helper: 'Suma de grupos del proceso'
          }
        ];

  topCards.forEach((card, index) => {
    const column = index % 2;
    const row = Math.floor(index / 2);
    const x =
      margin + progressCardWidth + gutter + column * (smallCardWidth + gutter);
    const y = margin + 98 + row * (topCardHeight + gutter);
    drawPdfCard(doc, x, y, smallCardWidth, topCardHeight, card);
  });

  const bottomCards = [
    {
      title: 'Grupos estudiantes',
      value: formatNumber(summary.studentGroups),
      helper: 'Grupos clasificados como estudiantes'
    },
    {
      title: 'Grupos docentes',
      value: formatNumber(summary.teacherGroups),
      helper: 'Grupos clasificados como docentes'
    },
    {
      title: 'Grupos comunidad',
      value: formatNumber(summary.communityGroups),
      helper: 'Grupos clasificados como comunidad'
    }
  ];

  bottomCards.forEach((card, index) => {
    const x = margin + index * (secondaryCardWidth + gutter);
    drawPdfCard(doc, x, margin + 308, secondaryCardWidth, bottomCardHeight, card);
  });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(90, 118, 136);
  doc.text(
    'Resumen exportado desde el panel de avances del proceso.',
    margin,
    pageHeight - 24
  );

  doc.save(fileName);
}

function createEmptyParticipationSummary() {
  return {
    territoriesIntervened: 0,
    schoolsIntervened: 0,
    characterizedPeople: 0,
    studentIntervened: 0,
    teacherIntervened: 0,
    totalGroups: 0,
    studentGroups: 0,
    teacherGroups: 0,
    communityGroups: 0
  };
}

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function countUniqueIntervenedSchoolsByName(schools) {
  const intervenedSchoolKeys = new Set();

  (Array.isArray(schools) ? schools : []).forEach((school, index) => {
    if (!Array.isArray(school?.groups) || school.groups.length === 0) {
      return;
    }

    const schoolName = String(school?.name ?? '').trim();
    const fallbackKey = String(school?.code ?? school?.id ?? index).trim();

    intervenedSchoolKeys.add(
      schoolName ? `name:${schoolName}` : `school:${fallbackKey || index}`
    );
  });

  return intervenedSchoolKeys.size;
}

function removeCancelledGroupsFromSchools(schools) {
  return (Array.isArray(schools) ? schools : []).map(school => ({
    ...school,
    groups: (Array.isArray(school?.groups) ? school.groups : []).filter(
      group => !isGroupCancelled(group)
    )
  }));
}

function getNormalizedGroupType(group) {
  const ageRange = String(group?.age_range ?? group?.ageRange ?? '').trim();
  if (ageRange) return normalizeText(ageRange);

  const legacyDescription = String(group?.description ?? '').trim();
  const normalizedLegacyDescription = normalizeText(legacyDescription);
  return ['docente', 'comunidad', 'estudiante'].some(type =>
    normalizedLegacyDescription.includes(type)
  )
    ? normalizedLegacyDescription
    : '';
}

function getGroupCode(group) {
  return String(group?.code || '').trim().toUpperCase();
}

function isTeacherGroup(group) {
  return (
    getNormalizedGroupType(group).includes('docent') ||
    TEACHER_GROUP_CODE_PATTERN.test(getGroupCode(group))
  );
}

function isCommunityGroup(group) {
  return getNormalizedGroupType(group).includes('comunidad');
}

function isStudentGroup(group) {
  const groupType = getNormalizedGroupType(group);

  if (groupType.includes('estudiant')) return true;
  if (isTeacherGroup(group) || isCommunityGroup(group)) return false;

  return Boolean(group) && STUDENT_GROUP_CODE_PATTERN.test(getGroupCode(group));
}

function isConfirmedBeneficiarySchool(beneficiary) {
  const school = beneficiary?.school ?? beneficiary?.group?.school ?? null;
  return school?.active !== false;
}

function buildParticipationSummary(schools, beneficiaries, scope) {
  const scopedSchools = removeCancelledGroupsFromSchools(
    buildScopedSchools(schools, scope)
  );
  const groupEntries = buildScopedGroupEntries(scopedSchools, scope);
  const allGroups = groupEntries.map(({ group }) => group);
  const knownCommuneIds = new Set(COMMUNE_LAYOUT.map(item => item.id));

  const groupCountsByTerritory = groupEntries.reduce((accumulator, entry) => {
    const territoryId = resolveGroupMunicipalityId(entry.group, entry.school);

    if (
      territoryId &&
      (knownCommuneIds.has(territoryId) || isMedellinTerritoryId(territoryId))
    ) {
      accumulator[territoryId] = (accumulator[territoryId] || 0) + 1;
    }

    return accumulator;
  }, {});
  const impactedTerritoryCount = Object.keys(groupCountsByTerritory).filter(id => {
    const territoryId = Number(id);

    return knownCommuneIds.has(territoryId) || isMedellinTerritoryId(territoryId);
  }).length;

  const filteredBeneficiaries = Array.isArray(beneficiaries)
    ? beneficiaries.filter(beneficiary =>
        matchesBeneficiaryByAdminScope(beneficiary, scope) &&
        isConfirmedBeneficiarySchool(beneficiary) &&
        !isGroupCancelled(beneficiary?.group)
      )
    : [];
  const characterizedBeneficiaries =
    scope === 'pf'
      ? filteredBeneficiaries.filter(beneficiary =>
          !isTeacherGroup(beneficiary?.group)
        )
      : filteredBeneficiaries;

  const teacherGroups = allGroups.filter(isTeacherGroup).length;
  const communityGroups = allGroups.filter(isCommunityGroup).length;
  const studentGroups = Math.max(
    allGroups.filter(isStudentGroup).length,
    allGroups.length - teacherGroups - communityGroups,
    0
  );
  const teacherIntervened = filteredBeneficiaries.filter(beneficiary =>
    isTeacherGroup(beneficiary?.group)
  ).length;
  const studentIntervened = filteredBeneficiaries.filter(beneficiary =>
    isStudentGroup(beneficiary?.group)
  ).length;

  return {
    territoriesIntervened: impactedTerritoryCount,
    schoolsIntervened: countUniqueIntervenedSchoolsByName(scopedSchools),
    characterizedPeople: characterizedBeneficiaries.length,
    studentIntervened,
    teacherIntervened,
    totalGroups: allGroups.length,
    studentGroups,
    teacherGroups,
    communityGroups
  };
}

function buildDashboardAnalysisPath(path, scope, dateFilters) {
  const scopedPath = buildAdminPathWithScope(path, scope);
  const [pathname, search = ''] = scopedPath.split('?');
  const nextSearchParams = new URLSearchParams(search);

  if (dateFilters?.dateFrom) {
    nextSearchParams.set('dateFrom', dateFilters.dateFrom);
  }

  if (dateFilters?.dateTo) {
    nextSearchParams.set('dateTo', dateFilters.dateTo);
  }

  const nextSearch = nextSearchParams.toString();
  return nextSearch ? `${pathname}?${nextSearch}` : pathname;
}

function GoalGauge({ loading, goalPercentage, totalGroups, processGoal }) {
  return (
    <div
      className='overview__goal-gauge'
      role='progressbar'
      aria-valuemin={0}
      aria-valuemax={processGoal}
      aria-valuenow={
        loading ? 0 : Math.min(totalGroups, processGoal)
      }
      aria-valuetext={
        loading
          ? 'Cargando avance de grupos'
          : `${totalGroups} de ${processGoal} grupos`
      }
      aria-label='Avance hacia la meta de grupos'>
      <svg
        className='overview__goal-gauge-svg'
        viewBox='0 0 240 140'
        aria-hidden='true'>
        <path
          className='overview__goal-gauge-track'
          d='M 30 120 A 90 90 0 0 1 210 120'
          pathLength='100'
        />
        <path
          className='overview__goal-gauge-fill'
          d='M 30 120 A 90 90 0 0 1 210 120'
          pathLength='100'
          style={{
            '--goal-progress': loading ? 0 : goalPercentage
          }}
        />
      </svg>

      <strong className='overview__goal-gauge-value'>
        {loading ? '--' : `${goalPercentage}%`}
      </strong>
    </div>
  );
}

function GoalStats({ loading, totalGroups, processGoal, extraClassName = '' }) {
  const groupsRemaining = Math.max(processGoal - totalGroups, 0);
  const className = extraClassName
    ? `overview__goal-stats ${extraClassName}`
    : 'overview__goal-stats';

  return (
    <div className={className}>
      <article className='overview__goal-stat'>
        <span>Meta</span>
        <strong>{processGoal}</strong>
      </article>

      <article className='overview__goal-stat'>
        <span>Creados</span>
        <strong>{loading ? '--' : totalGroups}</strong>
      </article>

      <article className='overview__goal-stat'>
        <span>Faltan</span>
        <strong>{loading ? '--' : groupsRemaining}</strong>
      </article>
    </div>
  );
}

function ParticipationSchoolsDashboard({ summary, loading, scope, dateFilters }) {
  const processGoal = PROCESS_GROUP_GOALS[scope] || PROCESS_GROUP_GOALS.pf;
  const isNadaDeNerviosScope = scope === 'ndn';
  const isGeneralScope = scope === 'general';
  const schoolsIntervenedLabel = isNadaDeNerviosScope
    ? 'Instituciones intervenidas'
    : isGeneralScope
      ? 'Instituciones intervenidas'
      : 'IE intervenidas';
  const goalCompletion = Math.min(
    summary.totalGroups / processGoal,
    1
  );
  const goalPercentage = Math.round(goalCompletion * 100);

  return (
    <div className='admin-progress-dashboard__schools schools-dashboard'>
      <section
        className={`schools-dashboard__overview overview ${
          isNadaDeNerviosScope ? 'overview--ndn' : 'overview--pf'
        }`}>
        {isGeneralScope ? (
          <div className='overview__general-heading'>
            <span>Panel general</span>
            <strong>Escuelas de Participacion + Nada de Nervios</strong>
          </div>
        ) : null}
        <div className='overview__panel'>
          <article className='overview__card overview__card--goal'>
            <div className='overview__goal-header'>
              <p className='overview__card-title'>Avance de meta de grupos</p>
              <p className='overview__card-subtitle'>
                Meta del proceso: {processGoal} grupos creados
              </p>
            </div>

            <div className='overview__goal-surface'>
              {isNadaDeNerviosScope ? (
                <div className='overview__goal-content overview__goal-content--ndn'>
                  <GoalStats
                    loading={loading}
                    totalGroups={summary.totalGroups}
                    processGoal={processGoal}
                    extraClassName='overview__goal-stats--ndn'
                  />

                  <div className='overview__goal-meter overview__goal-meter--ndn'>
                    <GoalGauge
                      loading={loading}
                      goalPercentage={goalPercentage}
                      totalGroups={summary.totalGroups}
                      processGoal={processGoal}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className='overview__goal-meter'>
                    <GoalGauge
                      loading={loading}
                      goalPercentage={goalPercentage}
                      totalGroups={summary.totalGroups}
                      processGoal={processGoal}
                    />
                  </div>

                  <GoalStats
                    loading={loading}
                    totalGroups={summary.totalGroups}
                    processGoal={processGoal}
                  />
                </>
              )}
            </div>
          </article>

          {isNadaDeNerviosScope ? (
            <div className='overview__metrics-grid overview__metrics-grid--ndn'>
              <article className='overview__card overview__card--compact'>
                <strong className='overview__metric-value'>
                  {loading ? '--' : summary.territoriesIntervened}
                </strong>
                <p className='overview__label'>Comunas intervenidas</p>
              </article>

              <article className='overview__card overview__card--compact'>
                <strong className='overview__metric-value'>
                  {loading ? '--' : summary.characterizedPeople}
                </strong>
                <p className='overview__label'>Personas caracterizadas</p>
              </article>

              <article className='overview__card overview__card--compact'>
                <strong className='overview__metric-value'>
                  {loading ? '--' : summary.schoolsIntervened}
                </strong>
                <p className='overview__label'>{schoolsIntervenedLabel}</p>
              </article>

              <article className='overview__card overview__card--compact'>
                <strong className='overview__metric-value'>
                  {loading ? '--' : summary.totalGroups}
                </strong>
                <p className='overview__label'>Total de grupos creados</p>
              </article>
            </div>
          ) : (
            <>
              <div className='overview__stack'>
                <article className='overview__card overview__card--compact'>
                  <strong className='overview__metric-value'>
                    {loading ? '--' : summary.territoriesIntervened}
                  </strong>
                  <p className='overview__label'>Comunas intervenidas</p>
                </article>

                <article className='overview__card overview__card--compact'>
                  <strong className='overview__metric-value'>
                    {loading ? '--' : summary.schoolsIntervened}
                  </strong>
                  <p className='overview__label'>{schoolsIntervenedLabel}</p>
                </article>
              </div>

              <article className='overview__card overview__card--metric'>
                <div className='overview__people-split'>
                  <div className='overview__people-item'>
                    <strong className='overview__metric-value overview__metric-value--large'>
                      {loading ? '--' : summary.studentIntervened}
                    </strong>
                    <p className='overview__label overview__label--people'>
                      Estudiantes intervenidos
                    </p>
                  </div>

                  <div className='overview__people-item'>
                    <strong className='overview__metric-value overview__metric-value--secondary'>
                      {loading ? '--' : summary.teacherIntervened}
                    </strong>
                    <p className='overview__label overview__label--people'>
                      Docentes intervenidos
                    </p>
                  </div>
                </div>
              </article>

              <article className='overview__card overview__card--groups'>
                <strong className='overview__metric-value overview__metric-value--large'>
                  {loading ? '--' : summary.totalGroups}
                </strong>
                <p className='overview__label overview__label--centered'>
                  Total de grupos creados
                </p>

                <div className='overview__group-split'>
                  <div className='overview__group-item'>
                    <strong>{loading ? '--' : summary.studentGroups}</strong>
                    <span>Grupos estudiantes</span>
                  </div>

                  <div className='overview__group-item'>
                    <strong>{loading ? '--' : summary.teacherGroups}</strong>
                    <span>Grupos docentes</span>
                  </div>
                </div>

                {!loading && summary.communityGroups > 0 ? (
                  <p className='overview__groups-note'>
                    Incluye {summary.communityGroups} grupos de comunidad.
                  </p>
                ) : null}
              </article>
            </>
          )}
        </div>
      </section>

      <section className='schools-dashboard__analysis analysis-grid'>
        {PARTICIPATION_ANALYSIS_CARDS.map(card => (
          card.path ? (
            <Link
              key={card.title}
              to={buildDashboardAnalysisPath(card.path, scope, dateFilters)}
              className='analysis-grid__card analysis-card analysis-card--link'>
              <span
                className='material-symbols-rounded analysis-card__icon'
                aria-hidden='true'>
                {card.icon}
              </span>
              <h2 className='analysis-card__title'>{card.title}</h2>
              <p className='analysis-card__text'>{card.description}</p>
            </Link>
          ) : (
            <article key={card.title} className='analysis-grid__card analysis-card'>
              <span
                className='material-symbols-rounded analysis-card__icon'
                aria-hidden='true'>
                {card.icon}
              </span>
              <h2 className='analysis-card__title'>{card.title}</h2>
              <p className='analysis-card__text'>{card.description}</p>
            </article>
          )
        ))}
      </section>
    </div>
  );
}

function GenericDashboard({ copy }) {
  return (
    <div className='admin-progress-dashboard__generic generic-dashboard'>
      <section className='generic-dashboard__hero'>
        <span className='generic-dashboard__eyebrow'>{copy.eyebrow}</span>
        <h1 className='generic-dashboard__title'>{copy.processLabel}</h1>
        <p className='generic-dashboard__description'>{copy.description}</p>
      </section>
    </div>
  );
}

function AdminProgressDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const adminScope = normalizeAdminScope(searchParams.get('scope'));
  const dateFilters = getDateFiltersFromSearchParams(searchParams);
  const dateFrom = dateFilters.dateFrom;
  const dateTo = dateFilters.dateTo;
  const hasProcessDashboard =
    adminScope === 'general' ||
    adminScope === 'pf' ||
    adminScope === 'ndn' ||
    adminScope === 'cs';
  const genericCopy =
    GENERIC_DASHBOARD_COPY[adminScope] || GENERIC_DASHBOARD_COPY.default;
  const [participationSummary, setParticipationSummary] = useState(
    createEmptyParticipationSummary
  );
  const [isParticipationLoading, setIsParticipationLoading] = useState(
    hasProcessDashboard
  );
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadParticipationDashboard() {
      if (!hasProcessDashboard) {
        setIsParticipationLoading(false);
        return;
      }

      setIsParticipationLoading(true);

      const [schoolsResponse, beneficiariesResponse] = await Promise.all([
        adminScope === 'cs' ? getControlSocialActivities() : getSchools(),
        getAllBeneficiaries()
      ]);

      if (!isMounted) {
        return;
      }

      const activeDateFilters = { dateFrom, dateTo };
      const normalizedSchools =
        adminScope === 'cs'
          ? (Array.isArray(schoolsResponse) ? schoolsResponse : []).map(
              activity => ({
                id: activity?.idActivity,
                code: activity?.name,
                name: activity?.line || activity?.name,
                programScope: 'CS',
                active: true,
                createdAt: activity?.createdAt,
                groups: (Array.isArray(activity?.groups)
                  ? activity.groups
                  : []
                ).map(group => ({
                  ...group,
                  municipalityId: group?.comuna,
                  school: {
                    id: activity?.idActivity,
                    code: activity?.name,
                    programScope: 'CS'
                  }
                }))
              })
            )
          : schoolsResponse;
      const dateFilteredSchools = filterSchoolsByGroupFirstMeetingDate(
        filterConfirmedSchools(normalizedSchools),
        activeDateFilters
      );
      const dateFilteredBeneficiaries = filterRecordsByDateRange(
        beneficiariesResponse,
        activeDateFilters,
        getRecordCreatedDate
      );

      setParticipationSummary(
        buildParticipationSummary(
          dateFilteredSchools,
          dateFilteredBeneficiaries,
          adminScope
        )
      );
      setIsParticipationLoading(false);
    }

    loadParticipationDashboard();

    return () => {
      isMounted = false;
    };
  }, [adminScope, dateFrom, dateTo, hasProcessDashboard]);

  function handleDateFilterChange(key, value) {
    setSearchParams(
      setDateFilterInSearchParams(searchParams, key, value),
      { replace: true }
    );
  }

  function handleClearDateFilters() {
    setSearchParams(clearDateFiltersFromSearchParams(searchParams), {
      replace: true
    });
  }

  function handleDownloadDashboard() {
    if (!hasProcessDashboard || isParticipationLoading) {
      return;
    }

    setIsDownloading(true);

    try {
      downloadParticipationDashboardPdf({
        scope: adminScope,
        summary: participationSummary,
        dateFilters
      });
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <section className='admin-progress-dashboard'>
      <div className='admin-progress-dashboard__shell'>
        {hasProcessDashboard ? (
          <div className='admin-progress-dashboard__toolbar'>
            <DateRangeFilter
              dateFrom={dateFilters.dateFrom}
              dateTo={dateFilters.dateTo}
              onChange={handleDateFilterChange}
              onClear={handleClearDateFilters}
              disabled={isParticipationLoading}
            />
            <button
              type='button'
              className='admin-progress-dashboard__download-button'
              onClick={handleDownloadDashboard}
              disabled={isParticipationLoading || isDownloading}>
              <span
                className='material-symbols-rounded'
                aria-hidden='true'>
                download
              </span>
              {isDownloading ? 'Preparando archivo...' : 'Descargar dashboard'}
            </button>
          </div>
        ) : null}

        {hasProcessDashboard ? (
          <ParticipationSchoolsDashboard
            summary={participationSummary}
            loading={isParticipationLoading}
            scope={adminScope}
            dateFilters={dateFilters}
          />
        ) : (
          <GenericDashboard copy={genericCopy} />
        )}
      </div>
    </section>
  );
}

export default AdminProgressDashboard;
